import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import {
  CreditCard as CreditCardIcon,
  DollarBill as DollarBillIcon,
} from '@factorialco/f0-react/icons/app'

import {
  dateBackendDateTimeToBackend,
  formatBackendDateToDayMonthYear,
} from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import TransactionsDetailsWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/TransactionsDetailsWidget'
import { isAutomaticExpensable } from 'modules/expenses/lib/expensables/expensable'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import tagColor from 'modules/expenses/lib/tagColors'
import { transactionsFromExpensable } from 'modules/expenses/lib/transactions/transactionsFromExpensable'
import { Expensable } from 'modules/expenses/types/Expensable'

import FormBlock from '../Sections/FormBlock'
import ExpensableAlerts from './ExpensableAlerts'
import ExpenseTransactionTag from './ExpenseTransactionTag'

interface Props {
  expensable?: Expensable
  canEditExpensesWithPendingStatus?: boolean
}

const Separator = () => (
  <Box
    height="s16"
    border={{
      left: {
        width: 's1',
        style: 'solid',
        color: 'grey400',
      },
    }}
  />
)

interface TagWithLabelProps {
  label: string
  value: string
}

const TagWithLabel = ({ label, value }: TagWithLabelProps) => (
  <Box flexDirection="row" gap="s2" alignItems="center">
    <Text.Normal size="200">{label}</Text.Normal>
    <F0TagRaw noBorder text={value} />
  </Box>
)

const VirtualCardTag = ({ last4 }: { last4: string }) => (
  <F0TagRaw text={`${i18n.t('expenses_v2.cards.type.virtual')} *${last4}`} icon={CreditCardIcon} />
)

const ManualCreationTag = () => (
  <F0TagRaw
    text={i18n.t('expenses_v2.forms.new_form.summary.fields.creation_manual.label')}
    icon={DollarBillIcon}
  />
)

const BasicDetails = ({ expensable, canEditExpensesWithPendingStatus }: Props) => {
  const { status, expense, reviewRequestAt, owner, reporter } = expensable ?? {}
  const hasVirtualCard = expense?.card?.cardType === 'virtual'
  const isManualCreation = !expensable || expense?.creationType === 'manual'
  const hasReviewRequest = Boolean(reviewRequestAt)
  const hasDifferentReporter = owner?.id !== reporter?.id
  const transactions = expensable ? transactionsFromExpensable(expensable) : []
  const hasTransactions = expensable
    ? isAutomaticExpensable(expensable) && transactions.length > 0
    : false

  const showSingleTransaction = hasTransactions && transactions.length === 1
  const showMultipleTransactions = hasTransactions && transactions.length > 1

  return (
    <FormBlock
      title={i18n.t('expenses_v2.forms.new_form.blocks.basic_details')}
      divider={false}
      noGap
    >
      <Box flexDirection="row" gap="s12" alignItems="center" flexWrap="wrap" paddingTop="s8">
        <Tag showCircle type={tagColor(status)} leftItem="circle">
          {getStatusLabel(status ?? 'draft')}
        </Tag>

        {hasVirtualCard && expense?.card?.last4 && (
          <>
            <Separator />
            <VirtualCardTag last4={expense.card.last4} />
          </>
        )}

        {isManualCreation && (
          <>
            <Separator />
            <ManualCreationTag />
          </>
        )}

        {hasReviewRequest && reviewRequestAt && (
          <>
            <Separator />
            <TagWithLabel
              label={i18n.t('expenses_v2.forms.new_form.summary.fields.review_request_at.label')}
              value={formatBackendDateToDayMonthYear(
                dateBackendDateTimeToBackend(reviewRequestAt),
                false
              )}
            />
          </>
        )}

        {hasDifferentReporter && reporter?.fullName && (
          <>
            <Separator />
            <TagWithLabel
              label={i18n.t('expenses_v2.forms.new_form.summary.fields.reporter.label')}
              value={reporter.fullName}
            />
          </>
        )}
      </Box>

      {showSingleTransaction && <ExpenseTransactionTag transaction={transactions[0]} />}

      {expensable && (
        <ExpensableAlerts
          expensable={expensable}
          canEditExpensesWithPendingStatus={canEditExpensesWithPendingStatus}
        />
      )}
      {showMultipleTransactions && expensable && (
        <TransactionsDetailsWidget expensable={expensable} />
      )}
    </FormBlock>
  )
}

export default BasicDetails
